<template>
  <BaseWorkPageLayout :title="pageTitle" icon="file-invoice">
    <template #cta>
      <InvoicingIndexActionDropDownComponent
        @generate-invoices-pdf="$refs.table?.handleGenerateInvoicesPDF()"
        @generate-invoices-csv="$refs.table?.handleGenerateInvoicesCSV()"
      />
    </template>
    <InvoicingIndexTable
      ref="table"
      :key="$route.name"
      :server-filters="true"
      :show-status-filter="list.showStatusFilter"
      :default-current-fiscal-year="list.defaultCurrentFiscalYear"
      v-if="metadata?.columns"
      :columns="metadata.columns"
      :metadata="metadata"
      :model-list="invoicesList"
      :search="true"
      :is-hoverable="true"
      :is-responsive="true"
      @row-click="redirectToModelPage"
      @refresh-click="fetchList"
      @server-filters-change="onServerFiltersChange"
    />
  </BaseWorkPageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import InvoicingIndexTable from './Table/InvoicingIndexTable'
import InvoicingIndexActionDropDownComponent from './Table/components/InvoicingIndexActionDropDownComponent.vue'

/**
 * How each list route queries the API. Every list loads its filters from
 * `api/invoicing/index-metadata` then the documents, filtered by the API.
 * - showStatusFilter: the unpaid/paid endpoints force the status.
 * - defaultCurrentFiscalYear: the API defaults to the current exercise,
 *   except for unpaid invoices (every exercise, overdue ones included).
 */
const LISTS = {
  invoice_unpaid_index_route: {
    endpoint: 'api/unpaid/invoicing',
    metadataDocType: 'INV',
    showStatusFilter: false,
    defaultCurrentFiscalYear: false
  },
  invoice_paid_index_route: {
    endpoint: 'api/paid/invoicing',
    metadataDocType: 'INV',
    showStatusFilter: false,
    defaultCurrentFiscalYear: true
  }
}

function listConfig(route) {
  return (
    LISTS[route.name] ?? {
      endpoint: 'api/invoicing',
      docType: route.meta.doc_type,
      metadataDocType: route.meta.doc_type,
      showStatusFilter: true,
      defaultCurrentFiscalYear: true
    }
  )
}

export default {
  name: 'TheInvoicingIndexPage',
  components: {
    InvoicingIndexTable,
    InvoicingIndexActionDropDownComponent
  },
  data() {
    return { serverFilterParams: {} }
  },
  computed: {
    list() {
      return listConfig(this.$route)
    },
    ...mapGetters({
      invoicesList: 'invoicing/getInvoiceIndex',
      metadata: 'invoicing/getInvoiceMetadata'
    }),
    title() {
      return this.$route.meta.doc_type
    },
    pageTitle() {
      switch (this.$route.name) {
        case 'invoice_unpaid_index_route':
          return this.$t('global.unpaid')
        case 'invoice_paid_index_route':
          return this.$t('global.paid')
        default:
          return this.$t('global.invoicing_type.' + this.$route.meta.doc_type)
      }
    }
  },
  methods: {
    ...mapActions({
      fetchInvoiceIndexMetadata: 'invoicing/fetchInvoiceIndexMetadata',
      fetchInvoiceIndex: 'invoicing/fetchInvoiceIndex'
    }),
    redirectToModelPage(id) {
      this.$router.push({
        name: 'invoice_info_route',
        params: { invoicing_id: id }
      })
    },
    /** Metadata (filters, columns) then the list: on page load. */
    async fetchAllInvoices() {
      this.serverFilterParams = {}
      await this.fetchInvoiceIndexMetadata(this.list.metadataDocType)
      await this.fetchList()
    },
    /** The list only: on filter change or refresh. */
    async fetchList() {
      await this.fetchInvoiceIndex({
        endpoint: this.list.endpoint,
        doc_type: this.list.docType,
        filters: this.serverFilterParams
      })
    },
    onServerFiltersChange(params) {
      this.serverFilterParams = params
      this.fetchList()
    }
  },
  watch: {
    '$route.name'(newValue) {
      this.fetchAllInvoices()
    }
  },
  mounted() {
    this.fetchAllInvoices()
  }
}
</script>
